import { type useEffect, type useRef, type useState } from "react";

import { defer } from "@moment/api-collab/observableToAsyncGenerator";

import {
	type JsxParam,
	type JsxSimpleObjectArrayParam,
	type JsxStringUnionParam,
} from "~/runtime/cell-compiler/analysis/JsxParameterInfo";

import { type CanvasRuntimeManager, type Result, type ResultsObserver } from ".";
import type { Snippet, SnippetHandle } from "./InspectFieldCodeSnippetHandle";

/**
 * This is a shimmed type to make it typecheck since `typeof VariableObserver` doesn't
 * include the constructor or the functions associated with the class. So here, we try
 * to set up the type to have the same definition as VariableObserver.
 */
export type VariableObserverType = ResultsObserver & {
	new (_setBoxedValue: (result?: Result) => void): VariableObserverType;
} & {
	dispose();
};

/**
 * This creates a React hook that's useable within compute cells.
 * We pass in everything that we need
 *
 * @param React The react core library, here we just need a few hooks which we explicitly typedef for type safety.
 * @param createCellID Create cell id, not _technically_ necessary; we just need a unique identifier per snippet
 * @param getRuntime Lazy load the runtime in.
 * @param snippetFromExpression This is from useSnippetEvaluation.ts to evaluate code cells
 * @param VariableObserver The VariableObserver class to be passed in so we can instantiate it as a snippet observer.
 * @returns a React hook that takes in an expression (code cell name) and returns its value
 */
export const makeUseCellValue = (
	React: {
		useState: typeof useState;
		useRef: typeof useRef;
		useEffect: typeof useEffect;
	},
	createCellID: (len?: number) => string,
	runtime: CanvasRuntimeManager,
	snippetFromExpression: (
		id: string,
		fieldType: JsxStringUnionParam | JsxSimpleObjectArrayParam | JsxParam,
		expression: string
	) => Promise<Snippet>,
	VariableObserver: VariableObserverType
) => {
	// This mimics useSnippetEvaluation
	return (expression: string) => {
		const fieldType: JsxParam = {
			type: "TSObjectKeyword",
			metadata: undefined,
		};
		const [id] = React.useState(() => createCellID());

		const [result, setResult] = React.useState<unknown>();
		const observer = React.useRef<VariableObserverType | undefined>();

		React.useEffect(() => {
			const _snippet = defer<Snippet | undefined>();
			const _snippetHandle = defer<SnippetHandle<object | undefined>>();

			const evaluate = async (
				fieldType: JsxStringUnionParam | JsxSimpleObjectArrayParam | JsxParam,
				expression: string
			) => {
				const snippet = await snippetFromExpression(id, fieldType, expression);
				_snippet.resolve(snippet);
				const snippetHandle = runtime?.getOrCacheSnippet(snippet);
				_snippetHandle.resolve(snippetHandle);

				//
				// IMPORTANT: Dispose old observer or it could continue to call `setBoxedValue`.
				//

				observer.current !== undefined && observer.current.dispose();

				//
				// And add a new variable observer!
				//

				observer.current = new VariableObserver((result) => {
					if (result?.type == "FULFILLED") {
						if (typeof result.value === "function") {
							//
							// IMPORTANT!! We *HAVE* to wrap `result.value` if it's a function.
							//
							// Passing a function to React state setters causes it to invoke it as an
							// atomic setter, e.g., `setCount((i) => i + 1)` would increment a count by
							// one. `setCount(() => 1)` in contrast sets the count to 1. This is
							// essentially what we're doing here, with a function.
							//

							setResult(() => result.value);
						} else {
							// Else, it's not a special value, just do the normal thing.
							setResult(result.value);
						}
					} else if (result?.type === "REJECTED") {
						setResult(undefined);
					}
				});

				if (observer.current === undefined) {
					throw new Error("observer.current is undefined");
				}

				snippetHandle?.setSubscriber(observer.current);
			};

			void evaluate(fieldType, expression);

			return () => {
				observer.current?.dispose();
				void _snippet.then((snippet) => {
					if (snippet) {
						runtime?.purgeCachedSnippet(snippet);
					}
				});
				void _snippetHandle.then((snippetHandle) => {
					snippetHandle?.dispose();
				});
			};
		}, [expression]);

		return result;
	};
};
